/**
 * 🆕 [Tâche 4 SESSION MAX 2026-06-09] Helpers purs Gantt extraits de
 * `GanttGridCalendrier.jsx` pour testabilité et réutilisation.
 *
 * Aucun helper n'utilise React, hooks, ou DOM. Tous reçoivent leurs dépendances
 * via paramètres et retournent des valeurs primitives ou objets simples.
 *
 * Tests unitaires : `/app/frontend/src/lib/__tests__/ganttHelpers.test.js`
 */

export function computeGridSpan(btFirstDate, btLastDate, weekStartDate) {
  const first = new Date(btFirstDate);
  const last = new Date(btLastDate);
  const weekStart = new Date(weekStartDate);
  const weekEnd = new Date(weekStartDate);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const segStart = first < weekStart ? weekStart : first;
  const segEnd = last > weekEnd ? weekEnd : last;
  if (segStart > weekEnd || segEnd < weekStart) return null;
  const dayMs = 24 * 60 * 60 * 1000;
  const colStart = Math.round((segStart - weekStart) / dayMs) + 1;
  const colEnd = Math.round((segEnd - weekStart) / dayMs) + 2;
  return { colStart, colEnd };
}

/**
 * Helper pur — résout la couleur de chef pour une cellule date donnée.
 * Phase 51 BIS part B : chef pas cédulé ce jour → slate-400 + flag isSansChef.
 *
 * 🛡️ [Phase 60 — 2026-06-16] Fix bug intermittent « Sans chef » sur cellule
 * isolée. Avant Phase 60, la garde `presents.length > 0` court-circuitait
 * `isSansChef` quand AUCUN employé n'était cédulé sur ce jour (cellule vide).
 * Résultat : cellule mercredi vide d'un BT Lun-Ven affichait la couleur du
 * chef au lieu de gris pâle « Sans chef ». La sémantique correcte est :
 * « si un chef est défini pour ce jour ET qu'il n'est PAS dans les présents,
 * c'est "Sans chef" — peu importe qu'il y ait d'autres peintres ou non ».
 *
 * 🎨 [Phase v13.53 — 2026-07-21] Uniformisation Sans chef → gris #94a3b8.
 * AVANT v13.53 : si `chef_equipe_id` était `null` du tout (aucun chef assigné
 * au BT), `chefDuJour` était falsy, `Boolean(chefDuJour) === false` → on
 * retombait sur `bt.color || '#3b82f6'`. Or `CalendrierProduction.getBonsForWeek`
 * calculait `bt.color = '#A855F7'` (mauve) dans ce cas → BT sans chef
 * s'affichait en MAUVE alors que « chef assigné + pas cédulé » s'affichait
 * en GRIS. Incohérence visuelle.
 * MAINTENANT : deux cas Sans chef traités uniformément → GRIS #94a3b8.
 *   - Cas A (existant Phase 60) : chef assigné mais pas dans les présents.
 *   - Cas B (v13.53) : aucun chef assigné du tout (chef_equipe_id falsy).
 * Exception : BT interne AVEC chef → couleur `bt.color` conservée (convention
 * `#64748B` slate-500 pour distinguer visuellement les BT internes).
 */
export function resolveCellColor(bt, date, presentsParJour = {}) {
  const chefDefault = bt.chef_equipe_id;
  const chefDuJour = (bt.chefs_par_jour || {})[date] || chefDefault;
  const presents = presentsParJour[date] || [];
  // 🎨 [v13.53] Cas B — aucun chef du tout (chef_equipe_id null/absent).
  // Exception : BT interne AVEC chef d'équipe conserve sa couleur `#64748B`
  // (défini par CalendrierProduction.getBonsForWeek). Sans chef, on tombe
  // ici même pour un BT interne → gris uniforme.
  if (!chefDuJour) return { color: '#94a3b8', isSansChef: true };
  const isSansChef = Boolean(chefDuJour) && !presents.includes(chefDuJour);
  if (isSansChef) return { color: '#94a3b8', isSansChef: true };
  const color = (bt.color_par_chef || {})[chefDuJour] || bt.color || '#3b82f6';
  return { color, isSansChef: false };
}

/**
 * 🆕 [Phase 54 D.4a] Helper pur — ordre stable des lanes (1 par chef + '_none').
 * Copie 1:1 de CalendarWeekRow.jsx L198-203.
 */
export function getGlobalLaneOrder(allBons, weekBons) {
  const laneOrder = [];
  const allChefIdsGlobal = [
    ...new Set((allBons || []).map(b => b.chef_equipe_id || '').filter(Boolean)),
  ].sort();
  allChefIdsGlobal.forEach(cid => laneOrder.push(cid));
  const hasNoChef =
    (weekBons || []).some(b => !b.chef_equipe_id) ||
    (allBons || []).some(b => !b.chef_equipe_id);
  if (hasNoChef) laneOrder.push('_none');
  return laneOrder;
}

/**
 * 🆕 [Phase 54 D.4a] Helper pur — packing greedy des BT en sous-rangées.
 * Copie 1:1 de CalendarWeekRow.jsx L215-226.
 */
export function packBonsIntoSubRows(laneBons) {
  const subRows = [];
  (laneBons || []).forEach(bon => {
    let placed = false;
    for (let r = 0; r < subRows.length; r++) {
      const canFit = subRows[r].every(existing =>
        bon.firstIdx > existing.lastIdx || bon.lastIdx < existing.firstIdx
      );
      if (canFit) { subRows[r].push(bon); placed = true; break; }
    }
    if (!placed) subRows.push([bon]);
  });
  return subRows;
}

/**
 * 🆕 [Phase 54 D.4b] Helper pur — construit le label affiché dans la carte BT.
 * Copie 1:1 de CalendarWeekRow.jsx L305-340.
 *
 * Hiérarchie :
 *   - Service Call → `🔧 {client} — {prénoms employés (≤3) ou first2+N}`
 *   - BT interne   → `🏢 {description_travaux || categorie_interne_nom || 'BT interne'} · {cat}`
 *   - BT régulier  → `{chefNom} / {client} / {projet}` (projet affiché si ≠ client)
 */
export function buildBonLabel(bon, dossierCache = {}) {
  const dossier = dossierCache[bon.dossier_id];
  const isServiceCall = bon.isServiceCall;
  const clientNom = bon.client_nom || dossier?.client_nom || '';
  const chefNom = bon.chef_equipe_nom || 'Sans chef';
  const nomProjet = bon.nom_dossier || dossier?.nom_dossier || '';
  const showProjet = nomProjet && nomProjet.trim() && nomProjet.trim() !== clientNom.trim();
  if (isServiceCall) {
    const scEmployeNoms = (bon.employes || [])
      .map(e => (e?.nom_complet || e?.nom || '').split(/\s+/)[0])
      .filter(Boolean);
    const scEmployeLabel = scEmployeNoms.length === 0
      ? chefNom
      : scEmployeNoms.length <= 3
        ? scEmployeNoms.join(', ')
        : `${scEmployeNoms.slice(0, 2).join(', ')} +${scEmployeNoms.length - 2}`;
    return `🔧 ${clientNom}${scEmployeLabel ? ` — ${scEmployeLabel}` : ''}`;
  }
  if (bon.est_interne) {
    const desc = bon.description_travaux || bon.categorie_interne_nom || 'BT interne';
    const cat = bon.categorie_interne_nom && bon.description_travaux
      ? ` · ${bon.categorie_interne_nom}`
      : '';
    return `🏢 ${desc}${cat}`;
  }
  return `${chefNom}${clientNom ? ` / ${clientNom}` : ''}${showProjet ? ` / ${nomProjet}` : ''}`;
}

/**
 * 🆕 [Phase 54 D.4b] Helper pur — calcule l'array d'emojis par jour dans le span d'un BT.
 * Copie 1:1 de CalendarWeekRow.jsx L361-406.
 *
 * Ordre des emojis par jour :
 *   0. `⛔` si bon.dates_fixes (sur CHAQUE jour)
 *   1. `🏗️` (U+1F3D7+FE0F) si nacelle requise ET ce jour ∈ equipements_nacelle_dates
 *   2. Emoji de chaque équipement (échafauds, lavage, etc.) si ce jour ∈ dates_utilisation,
 *      en excluant les entrées dont le nom contient "nacelle" (ancien format ignoré).
 */
export function computeBonEmojiCells(bon, spanDates) {
  const nacelleDatesSet = new Set(
    (bon.equipements_nacelle_dates || [])
      .map(d => typeof d === 'string' ? d : (d?.date || ''))
      .filter(Boolean)
  );
  const cells = spanDates.map(dayStr => {
    const dayEmojis = [];
    const seen = new Set();
    if (bon.dates_fixes) {
      dayEmojis.push('⛔');
      seen.add('__dates_fixes__');
    }
    if (bon.equipements_nacelle_requise && nacelleDatesSet.has(dayStr)) {
      dayEmojis.push('\u{1F3D7}\uFE0F');
      seen.add('__nacelle__');
    }
    for (const eq of (bon.equipements || [])) {
      if (!(eq.dates_utilisation || []).includes(dayStr)) continue;
      const nom = (eq.equipement_nom || '').toLowerCase();
      if (nom.includes('nacelle')) continue;
      const key = eq.equipement_id || nom;
      if (seen.has(key)) continue;
      seen.add(key);
      if (eq.emoji) dayEmojis.push(eq.emoji);
    }
    return dayEmojis;
  });
  const hasAnyEmojis = cells.some(arr => arr.length > 0);
  return { emojiCells: cells, hasAnyEmojis };
}

/**
 * 🆕 [Phase 54 D.4b] Helper pur — extrait les punches actifs d'un BT.
 * Copie 1:1 de CalendarWeekRow.jsx L346-347.
 */
export function getActivePunches(bon, punchesCache = {}) {
  const bonPunches = punchesCache[bon.id] || [];
  return bonPunches.filter(p => p.statut === 'en_cours');
}

/**
 * 🆕 [Phase 54 D.4c] Helper pur — extrait les punches terminés (validés) d'un BT.
 * Copie 1:1 de CalendarWeekRow.jsx L349-353 (hotfix 2026-05-03 alignement OngletStatistiques).
 */
export function getCompletedPunches(bon, punchesCache = {}) {
  const bonPunches = punchesCache[bon.id] || [];
  return bonPunches.filter(p =>
    p.statut !== 'en_cours'
    && p.statut !== 'refuse'
    && p.valide !== false
  );
}

/**
 * 🆕 [Phase 54 D.4d] Helper pur — déterminer si ce segment est le DERNIER du BT (mode 1 resize).
 * Copie 1:1 de CalendarWeekRow.jsx L684-689.
 *
 * @param {Object} bon       BT (avec dates_chantier[])
 * @param {string} segLastDate  Dernière date ISO de ce segment (ex. spanDates[length-1])
 * @returns {boolean}
 */
export function isLastSegmentInBon(bon, segLastDate) {
  const bonDates = [...(bon.dates_chantier || [])].sort();
  const lastBonDate = bonDates[bonDates.length - 1];
  return Boolean(lastBonDate && segLastDate && lastBonDate === segLastDate);
}

/**
 * 🆕 [Phase 54 D.4f] Helper pur — filtre les RDV d'un chef pour les jours d'une semaine.
 * Copie 1:1 de CalendarWeekRow.jsx L269-275.
 *
 * @param {Array} agendaEvents  Tous les RDV (state global)
 * @param {string} laneKey      chef_equipe_id (UUID) — '_none' filtré
 * @param {Array<string>} weekDateStrs  Liste des 7 dates ISO de la semaine
 * @returns {Array}  RDV où le chef est propriétaire OU invité accepté
 */
export function filterChefRdvs(agendaEvents, laneKey, weekDateStrs) {
  if (laneKey === '_none' || !agendaEvents) return [];
  return agendaEvents.filter(ev => {
    const isOwner = ev.employe_id === laneKey;
    const isInvited = (ev.invites || []).some(
      inv => inv.employee_id === laneKey && inv.statut === 'accepte'
    );
    return (isOwner || isInvited) && weekDateStrs.includes(ev.date);
  });
}


/**
 * 🆕 [Phase 54 D.4b] Helper pur — détermine le badge Position Phase 51 BIS A
 * pour un BT donné dans un segment de dates.
 * Copie 1:1 de CalendarWeekRow.jsx L499-526.
 *
 * @returns {{ position, date } | null} null si aucune collision équipe sur ce span.
 */
export function getPositionBadge(bon, teamId, spanDates, cellMap) {
  if (!teamId || !cellMap) return null;
  for (const d of spanDates) {
    const positions = cellMap.get(`${teamId}__${d}`);
    if (positions) {
      const mine = positions.find(p => p.bt_id === bon.id);
      if (mine) return { position: mine.position, date: d };
    }
  }
  return null;
}

